import { useState, useEffect, useCallback } from "react";
import {
  _get_admin_businesses,
  _approve_admin_business,
  _reject_admin_business,
  _update_admin_business_status,
  _get_admin_available_businesses,
  _create_admin_support_manager,
  _update_admin_support_manager,
  _delete_admin_support_manager,
  _assign_businesses_to_manager,
  _bulk_assign_admin_businesses,
  _get_admin_bikers,
  _approve_admin_biker,
  _reject_admin_biker,
  _get_support_staffs,
  _create_support_staff,
  _update_support_staff,
  _delete_support_staff,
  _assign_zone_to_staff,
  _get_wallet_lookup_by_account_id,
  _get_admin_zones,
  _update_admin_business_special_status,
  _verify_admin_user,
} from "../../../common/axios_services";

const useAdminData = () => {
  const [availableBusinesses, setAvailableBusinesses] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [supportStaffs, setSupportStaffs] = useState([]);
  const [supportManagers, setSupportManagers] = useState([]);
  const [bikers, setBikers] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchBusinesses = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);

      const response = await _get_admin_businesses(params);
      setBusinesses(response.data?.data);
      return response.data?.data;
    } catch (err) {
      setError(err.message || "Failed to load businesses");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAvailableBusinesses = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);

      const response = await _get_admin_available_businesses(params);
      setAvailableBusinesses(response.data.data || []);
      return response.data;
    } catch (err) {
      setError(err.message || "Failed to load businesses");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAssignmentBusinesses = useCallback(
    async (assignedIds = [], params = {}) => {
      try {
        const response = await _get_admin_available_businesses(params);
        const data = response.data.data?.data || response.data.data || [];
        const filtered = data.filter(
          (business) => !assignedIds.includes(business.id),
        );

        return {
          data: filtered,
          pagination: response.data.pagination || {},
        };
      } catch (err) {
        setError(err.message || "Failed to load businesses");
        throw err;
      }
    },
    [],
  );

  const approveBusiness = useCallback(
    async (businessId) => {
      try {
        await _approve_admin_business(businessId);
        await fetchBusinesses();
      } catch (err) {
        throw err;
      }
    },
    [fetchBusinesses],
  );

  const rejectBusiness = useCallback(
    async (businessId) => {
      try {
        await _reject_admin_business(businessId);
        await fetchBusinesses();
      } catch (err) {
        throw err;
      }
    },
    [fetchBusinesses],
  );

  const updateBusinessStatus = useCallback(
    async (businessId, status) => {
      try {
        await _update_admin_business_status(businessId, status);
        await fetchBusinesses();
      } catch (err) {
        throw err;
      }
    },
    [fetchBusinesses],
  );

  const updateBusinessSpecialStatus = useCallback(
    async (businessId, isSpecial) => {
      try {
        await _update_admin_business_special_status(businessId, isSpecial);
        await fetchBusinesses();
      } catch (err) {
        throw err;
      }
    },
    [fetchBusinesses],
  );

  const fetchSupportStaffs = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);

      const response = await _get_support_staffs(params);
      const staffData =
        response?.data?.data?.data || response?.data?.data || [];
      setSupportStaffs(staffData);
      const managers = staffData.filter(
        (staff) => staff.role === "SUPPORT_MANAGER",
      );
      setSupportManagers(managers);
      return response?.data;
    } catch (err) {
      setError(err.message || "Failed to load support staffs");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createSupportManager = useCallback(
    async (managerData) => {
      try {
        await _create_admin_support_manager(managerData);
        await fetchSupportStaffs();
      } catch (err) {
        throw err;
      }
    },
    [fetchSupportStaffs],
  );

  const updateSupportManager = useCallback(
    async (managerId, managerData) => {
      try {
        await _update_admin_support_manager(managerId, managerData);
        await fetchSupportStaffs();
      } catch (err) {
        throw err;
      }
    },
    [fetchSupportStaffs],
  );

  const deleteSupportManager = useCallback(
    async (managerId) => {
      try {
        await _delete_admin_support_manager(managerId);
        await fetchSupportStaffs();
      } catch (err) {
        throw err;
      }
    },
    [fetchSupportStaffs],
  );

  const assignBusinessesToManager = useCallback(
    async (managerId, businessIds) => {
      try {
        await _assign_businesses_to_manager(managerId, businessIds);
        await fetchSupportStaffs();
        await fetchAvailableBusinesses();
      } catch (err) {
        throw err;
      }
    },
    [fetchSupportStaffs],
  );

  const bulkAssignBusinesses = useCallback(
    async (assignments) => {
      try {
        const response = await _bulk_assign_admin_businesses(assignments);
        await fetchSupportStaffs();
        await fetchAvailableBusinesses();
        return response.data;
      } catch (err) {
        throw err;
      }
    },
    [fetchSupportStaffs, fetchBusinesses],
  );

  const fetchBikers = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);

      const response = await _get_admin_bikers(params);
      setBikers(response.data.data || []);
      return response.data;
    } catch (err) {
      setError(err.message || "Failed to load bikers");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const approveBiker = useCallback(
    async (bikerId) => {
      try {
        await _approve_admin_biker(bikerId);
        await fetchBikers();
      } catch (err) {
        throw err;
      }
    },
    [fetchBikers],
  );

  const rejectBiker = useCallback(
    async (bikerId) => {
      try {
        await _reject_admin_biker(bikerId);
        await fetchBikers();
      } catch (err) {
        throw err;
      }
    },
    [fetchBikers],
  );

  const createSupportStaff = useCallback(
    async (staffData) => {
      try {
        await _create_support_staff(staffData);
        await fetchSupportStaffs();
      } catch (err) {
        throw err;
      }
    },
    [fetchSupportStaffs],
  );

  const updateSupportStaff = useCallback(
    async (staffId, staffData) => {
      try {
        await _update_support_staff(staffId, staffData);
        await fetchSupportStaffs();
      } catch (err) {
        throw err;
      }
    },
    [fetchSupportStaffs],
  );

  const deleteSupportStaff = useCallback(
    async (staffId) => {
      try {
        await _delete_support_staff(staffId);
        await fetchSupportStaffs();
      } catch (err) {
        throw err;
      }
    },
    [fetchSupportStaffs],
  );

  const assignZoneToStaff = useCallback(
    async (staffId, zoneId) => {
      try {
        await _assign_zone_to_staff(staffId, zoneId);
        await fetchSupportStaffs();
      } catch (err) {
        throw err;
      }
    },
    [fetchSupportStaffs],
  );

  const lookupUserByAccountId = useCallback(async (accountId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await _get_wallet_lookup_by_account_id(accountId);
      return response?.data?.data || response?.data;
    } catch (err) {
      setError(err.message || "Failed to lookup user");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyUser = useCallback(async (userId) => {
    try {
      setLoading(true);
      setError(null);
      await _verify_admin_user(userId);
    } catch (err) {
      setError(err.message || "Failed to verify user");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchZones = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await _get_admin_zones(params);
      setZones(response?.data?.data?.data || response?.data?.data || []);
      return response?.data?.data || response?.data || [];
    } catch (err) {
      setError(err.message || "Failed to load zones");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBusinesses();
    fetchSupportStaffs();
    fetchAvailableBusinesses();
    fetchBikers();
    fetchSupportStaffs();
    fetchZones();
  }, [
    fetchBusinesses,
    fetchSupportStaffs,
    fetchAvailableBusinesses,
    fetchBikers,
    fetchSupportStaffs,
    fetchZones,
  ]);

  return {
    businesses,
    availableBusinesses,
    supportManagers,
    supportStaffs,
    bikers,
    zones,
    loading,
    error,
    fetchBusinesses,
    approveBusiness,
    rejectBusiness,
    updateBusinessStatus,
    updateBusinessSpecialStatus,
    fetchSupportStaffs,
    createSupportManager,
    updateSupportManager,
    deleteSupportManager,
    assignBusinessesToManager,
    bulkAssignBusinesses,
    fetchSupportStaffs,
    createSupportStaff,
    updateSupportStaff,
    deleteSupportStaff,
    assignZoneToStaff,
    fetchBikers,
    approveBiker,
    rejectBiker,
    fetchAssignmentBusinesses,
    lookupUserByAccountId,
    verifyUser,
    fetchZones,
  };
};

export default useAdminData;
